import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Printer, CircleNotch, Ticket, DownloadSimple, MagicWand } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function twoDigitWords(n) {
  if (n < 20) return ONES[n];
  return TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "");
}

function threeDigitWords(n) {
  if (n >= 100) return ONES[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + twoDigitWords(n % 100) : "");
  return twoDigitWords(n);
}

// Indian numbering (Lakh/Crore) - standard for GST documents.
function amountInWords(amount) {
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  if (rupees === 0 && paise === 0) return "Zero Rupees Only";
  let n = rupees;
  const parts = [];
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const hundred = n;
  if (crore) parts.push(threeDigitWords(crore) + " Crore");
  if (lakh) parts.push(threeDigitWords(lakh) + " Lakh");
  if (thousand) parts.push(threeDigitWords(thousand) + " Thousand");
  if (hundred) parts.push(threeDigitWords(hundred));
  let words = (parts.join(" ") || "Zero") + " Indian Rupees";
  if (paise) words += " and " + twoDigitWords(paise) + " Paise";
  return words + " Only";
}

function formatDateDMY(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split("-");
  return d && m && y ? `${d}-${m}-${y}` : isoDate;
}

// Aug 27 2026, user's explicit ask: real registered address/GSTIN/PAN per
// site, straight from the ERP portal's own "comp" table (see
// stock_transfer_service.get_delivery_note_data) - replaces the old
// single hardcoded company block, so the letterhead now correctly shows
// either "RADISH TECHNOLOGIES" or "RAY INTERNATIONAL" depending on which
// company the Ship-from site actually belongs to.
function CompanyBlock({ company, fallbackSiteId }) {
  if (!company) return <p className="text-[#475467]">{fallbackSiteId}</p>;
  // Aug 27 2026 fix: the ERP's Cadd1/Cadd2 free-text lines already
  // contain the city + PIN for every site in this tenant's `comp` table
  // (confirmed live) - a separate "city - pin" line only ever duplicated
  // it (e.g. P1 printed "...ALIGARH-UP-202001" then "ALIGARH - 202001"
  // again right below), so it's dropped rather than shown a second time.
  return (
    <>
      <p className="text-[#475467]">{[company.address_line1, company.address_line2].filter(Boolean).join(", ")}</p>
      <p className="text-[#475467]">GSTIN: {company.gstin || "—"} &nbsp; PAN: {company.pan || "—"}</p>
      <p className="text-[#475467]">State: {company.state || "—"} &nbsp; State Code: {company.state_code || "—"}</p>
    </>
  );
}

// Aug 29 2026, user's explicit ask: GST Rule 48(4) requires exactly these
// 3 labelled copies for goods in transit - printed as a small stamp on
// the document itself, not just a filename/UI label.
const COPY_TYPES = [
  { value: "original", label: "Original for Recipient" },
  { value: "duplicate", label: "Duplicate for Transporter" },
  { value: "triplicate", label: "Triplicate for Supplier" },
];

function CopyStamp({ label }) {
  return (
    <p className="text-right text-xs font-bold uppercase tracking-wide text-[#475467] mb-1" data-testid="delivery-note-copy-stamp">
      {label}
    </p>
  );
}

// Sep 2026, user's explicit ask (annotated PDF): Chrome's own default
// print header/footer (timestamp, page title, page URL) was showing up
// on the printed Delivery Challan - not something this page's own
// markup ever renders, purely the browser's print-dialog default. The
// only page-side fix is `@page { margin: 0 }`, which starves Chrome of
// the margin space it draws that header/footer text into - compensated
// with the document's own `print:p-10` below so content still isn't
// flush against the paper edge.
function PrintPageStyle() {
  return <style>{"@media print { @page { margin: 0; size: auto; } }"}</style>;
}

function DeliveryNoteDocument({ data, companyName, copyLabel, isAdmin, resolvingProductId, onResolveRate }) {
  return (
    <div className="max-w-[820px] mx-auto bg-white border border-[#D0D5DD] shadow-sm p-8 text-[13px] text-[#101828] print:border-0 print:shadow-none print:p-10" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="delivery-note-document">
      <CopyStamp label={copyLabel} />
      <div className="flex justify-between items-start border-b-2 border-[#101828] pb-3">
        <div>
          <h1 className="text-xl font-bold tracking-wide" data-testid="delivery-note-company-name">{companyName}</h1>
          <p className="text-[#475467] font-medium">{data.ship_from_site_id}</p>
          <CompanyBlock company={data.ship_from_company} fallbackSiteId={data.ship_from_site_id} />
        </div>
        <div className="text-right">
          <h2 className="text-lg font-bold uppercase">Delivery Challan</h2>
          <p className="text-[#667085] text-xs">(Stock Transfer / Bill of Supply)</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 mt-4">
        <div>
          <p><span className="font-bold">Serial Number:</span> <span data-testid="delivery-note-serial-number">{data.serial_number || "—"}</span></p>
          <p><span className="font-bold">Date of Issue:</span> {formatDateDMY(data.date_of_supply) || "—"}</p>
        </div>
        <div className="border border-[#D0D5DD] rounded-sm p-2">
          <p className="font-bold uppercase text-xs mb-1 text-[#475467]">Transport Details</p>
          <p>Vehicle No: {data.vehicle_no || "—"}</p>
          <p>G.R. No: {data.gr_no || "—"}</p>
          <p>Mode: {data.transportation_mode || "—"}</p>
          <p>Place of Supply: {data.place_of_supply || "—"}</p>
          <p data-testid="delivery-note-freight-forwarder">Freight Forwarder: {data.freight_forwarder || "Self"}</p>
          {data.remark && <p data-testid="delivery-note-remark">Remark: {data.remark}</p>}
          <p data-testid="delivery-note-sap-outbound-no">Sap outbound no: {data.sap_outbound_delivery_no || "—"}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 mt-4">
        <div className="border border-[#D0D5DD] rounded-sm p-2">
          <p className="font-bold uppercase text-xs mb-1 text-[#475467]">Details of Receiver | Billed to</p>
          <p className="font-bold">{data.ship_to_company?.company_name || data.ship_to_site_id}</p>
          <p className="text-[#475467] font-medium">{data.ship_to_site_id}</p>
          <CompanyBlock company={data.ship_to_company} fallbackSiteId={data.ship_to_site_id} />
        </div>
        <div className="border border-[#D0D5DD] rounded-sm p-2">
          <p className="font-bold uppercase text-xs mb-1 text-[#475467]">Details of Consignee | Shipped to</p>
          <p className="font-bold">{data.ship_to_company?.company_name || data.ship_to_site_id}</p>
          <p className="text-[#475467] font-medium">{data.ship_to_site_id}</p>
          <CompanyBlock company={data.ship_to_company} fallbackSiteId={data.ship_to_site_id} />
        </div>
      </div>

      <table className="w-full mt-4 border-collapse text-[11px]" data-testid="delivery-note-items-table">
        <thead>
          <tr>
            {["Sr.", "Part Code", "Description", "HSN", "Qty", "Unit", "Rate", "Amount"].map((h) => (
              <th key={h} className="border border-[#101828] bg-[#EAECF0] p-1.5 text-left font-bold">{h}</th>
            ))}
            {isAdmin && <th className="border border-[#101828] bg-[#EAECF0] p-1.5 text-left font-bold print:hidden"></th>}
          </tr>
        </thead>
        <tbody>
          {data.items.map((it, idx) => {
            // Nov 2026, user's explicit ask ("Create a admin button for
            // resolve 0 rate issue ... only for those who have blank
            // HSN & 0 rate") - initially AND'd (an item with BOTH
            // problems). Nov 2026 follow-up, user's explicit correction
            // ("update button removed without cross check") - a
            // PARTIAL fix (e.g. HSN resolved but rate still genuinely
            // 0.00) made `needsResolving` false under the old AND logic,
            // so the button vanished while rate was STILL broken, with
            // no way left to retry it. Now OR'd - stays visible as long
            // as EITHER one is still actually wrong.
            const needsResolving = !it.hsn_code || !it.rate;
            return (
              <tr key={it.product_id}>
                <td className="border border-[#D0D5DD] p-1.5">{idx + 1}</td>
                <td className="border border-[#D0D5DD] p-1.5 font-medium">{it.product_id}</td>
                <td className="border border-[#D0D5DD] p-1.5">{it.description || "—"}</td>
                <td className="border border-[#D0D5DD] p-1.5" data-testid={`delivery-note-hsn-${it.product_id}`}>{it.hsn_code || "—"}</td>
                <td className="border border-[#D0D5DD] p-1.5 text-right">{it.qty}</td>
                <td className="border border-[#D0D5DD] p-1.5">{it.unit}</td>
                <td className="border border-[#D0D5DD] p-1.5 text-right" data-testid={`delivery-note-rate-${it.product_id}`}>
                  ₹{it.rate.toFixed(2)}
                  {it.rate_is_cross_site_fallback && (
                    <span
                      className="text-[#B54708] ml-0.5"
                      title={`SAP has no priced valuation for ${it.product_id} at ${data.ship_from_site_id} itself - this is the best available rate from another site's valuation.`}
                      data-testid={`delivery-note-rate-fallback-marker-${it.product_id}`}
                    >
                      *
                    </span>
                  )}
                </td>
                <td className="border border-[#D0D5DD] p-1.5 text-right font-medium">₹{it.amount.toFixed(2)}</td>
                {isAdmin && (
                  <td className="border border-[#D0D5DD] p-1 text-center print:hidden">
                    {needsResolving && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[10px] border-[#B54708] text-[#B54708] hover:bg-[#FFFAEB]"
                        disabled={resolvingProductId === it.product_id}
                        onClick={() => onResolveRate(it.product_id)}
                        data-testid={`delivery-note-resolve-rate-btn-${it.product_id}`}
                      >
                        {resolvingProductId === it.product_id ? <CircleNotch size={12} className="animate-spin" /> : <><MagicWand size={12} className="mr-1" />Apply Latest Rate</>}
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
          <tr>
            <td colSpan={7} className="border border-[#D0D5DD] p-1.5 text-right font-bold">Total (INR)</td>
            <td className="border border-[#D0D5DD] p-1.5 text-right font-bold" data-testid="delivery-note-total">₹{data.total_amount.toFixed(2)}</td>
            {isAdmin && <td className="border border-[#D0D5DD] p-1.5 print:hidden"></td>}
          </tr>
        </tbody>
      </table>

      <p className="mt-3"><span className="font-bold">Total Amount (in words):</span> {amountInWords(data.total_amount)}</p>

      {data.items.some((it) => it.rate_is_cross_site_fallback) && (
        <p className="mt-1 text-[11px] text-[#475467]" data-testid="delivery-note-fallback-rate-footnote">
          * Rate shown is the best available SAP valuation from another site - this site itself has no priced valuation for this item in SAP yet.
        </p>
      )}

      <div className="mt-5">
        <p className="font-bold uppercase text-xs mb-1 text-[#475467]">Terms & Condition</p>
        <p>1) This is a Stock Transfer document issued for e-way bill / GST purposes, not a Tax Invoice.</p>
        <p>2) Goods must be packed and inspected in good condition upon receipt.</p>
        <p>3) All disputes are subject to Aligarh Jurisdiction only.</p>
      </div>

      <div className="flex justify-between items-end mt-10 pt-6">
        <p className="text-xs text-[#667085]">Certified that the particulars given above are true and correct.</p>
        <div className="text-center">
          <p className="font-bold">For {companyName}</p>
          <p className="mt-8 text-xs text-[#667085]">(Authorized Signatory)</p>
        </div>
      </div>
    </div>
  );
}

export default function DeliveryNotePage() {
  const { stoId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copyType, setCopyType] = useState("original");
  const [resolvingProductId, setResolvingProductId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stock-transfer/${stoId}/delivery-note`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load this delivery note."));
  }, [stoId]);

  if (error) return <div className="p-10 text-center text-[#912018]" data-testid="delivery-note-error">{error}</div>;
  if (!data) return <div className="p-10 flex justify-center"><CircleNotch size={24} className="animate-spin text-[#667085]" /></div>;

  const companyName = data.ship_from_company?.company_name || `RADISH TECHNOLOGIES-${data.ship_from_site_id}`;
  const copiesToRender = copyType === "all" ? COPY_TYPES : COPY_TYPES.filter((c) => c.value === copyType);

  // Excel export (Sep 2 2026, user's explicit ask: "print of delivery
  // note in excel same as pdf"; Sep 3 2026, user's explicit ask - "not
  // printable for A4" - moved to a server-generated file via openpyxl,
  // see delivery_note_excel_service.py, since the client-side xlsx
  // (SheetJS community edition) library silently drops page setup on
  // write).
  const exportExcel = () => {
    window.open(`${API}/stock-transfer/${stoId}/delivery-note/excel`, "_blank");
  };

  // Nov 2026, user's explicit ask ("Create a admin button for resolve
  // 0 rate issue ... when admin apply latest rate you need to fetch
  // current inventory rate of that item's & HSN") - re-fetches live SAP
  // rate+HSN for this one item, then replaces `data` with the server's
  // freshly-recomputed response (same shape as the initial GET).
  // Nov 2026 follow-up ("fix it" - the site-scoped rate genuinely IS
  // ₹0.00 in SAP at this exact site, but a real rate exists elsewhere -
  // see resolve_delivery_note_item_rate's own cross-site fallback) -
  // the toast now discloses when the applied rate came from another
  // site rather than this one's own book value (matches the `*` marker
  // shown next to the rate itself).
  const resolveRate = async (productId) => {
    const before = data.items.find((it) => it.product_id === productId);
    setResolvingProductId(productId);
    try {
      const { data: refreshed } = await axios.post(`${API}/stock-transfer/${stoId}/delivery-note/resolve-item-rate`, { product_id: productId });
      setData(refreshed);
      const after = refreshed.items.find((it) => it.product_id === productId);
      const rateFixed = !before.rate && after.rate > 0;
      const hsnFixed = !before.hsn_code && after.hsn_code;
      const fallbackNote = after.rate_is_cross_site_fallback ? ` (SAP has no priced valuation at ${data.ship_from_site_id} itself - this is the best rate found at another site)` : "";
      if (rateFixed && hsnFixed) toast.success(`Rate (₹${after.rate.toFixed(2)}${fallbackNote}) and HSN (${after.hsn_code}) both updated from SAP.`);
      else if (rateFixed) toast.success(`Rate updated to ₹${after.rate.toFixed(2)}${fallbackNote} from SAP. HSN is still blank in SAP for this item.`);
      else if (hsnFixed) toast.warning(`HSN updated to ${after.hsn_code}, but SAP still shows ₹0.00 valuation for ${productId} at ${data.ship_from_site_id} - that still needs fixing in SAP's own Valuation data.`);
      else toast.success(`Re-fetched from SAP for ${productId}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve this item's rate/HSN.");
    } finally {
      setResolvingProductId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F4F7] py-6 print:bg-white print:py-0" data-testid="delivery-note-page">
      <PrintPageStyle />
      <div className="max-w-[820px] mx-auto mb-4 flex justify-end gap-2 print:hidden">
        <select
          value={copyType}
          onChange={(e) => setCopyType(e.target.value)}
          className="border border-[#D0D5DD] rounded-md px-3 text-sm bg-white"
          data-testid="delivery-note-copy-type-select"
        >
          {COPY_TYPES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
          <option value="all">All 3 Copies (Original + Duplicate + Triplicate)</option>
        </select>
        <Button
          variant="outline"
          onClick={() => window.open(`/inventory/inter-plant-transfer/${stoId}/gate-pass`, "_blank")}
          data-testid="delivery-note-print-gate-pass-btn"
        >
          <Ticket size={16} className="mr-2" /> Print Gate Pass
        </Button>
        <Button onClick={() => window.print()} data-testid="delivery-note-print-btn" className="bg-[#175CD3] hover:bg-[#164FB0]">
          <Printer size={16} className="mr-2" /> Print / Save as PDF
        </Button>
        <Button onClick={exportExcel} variant="outline" data-testid="delivery-note-export-excel-btn">
          <DownloadSimple size={16} className="mr-2" /> Export as Excel
        </Button>
      </div>

      {copiesToRender.map((c, idx) => (
        <div key={c.value} className={idx < copiesToRender.length - 1 ? "print:break-after-page" : ""}>
          <DeliveryNoteDocument data={data} companyName={companyName} copyLabel={c.label} isAdmin={isAdmin} resolvingProductId={resolvingProductId} onResolveRate={resolveRate} />
          {idx < copiesToRender.length - 1 && <div className="h-6 print:hidden" />}
        </div>
      ))}
    </div>
  );
}
